import { useEffect, useState } from "react";
import { Briefcase, CalendarClock, History, IndianRupee, LogOut, PenLine, UserPlus, type LucideIcon } from "lucide-react";

import { api, errMsg } from "../../lib/api.js";
import { logError } from "../../lib/logger.js";
import { cn } from "../../lib/utils.js";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog.js";
import { Skeleton } from "../ui/skeleton.js";
import { ErrorBanner } from "./ReportKit.js";

type Kind = "JOIN" | "FIELD" | "COMP" | "SHIFT" | "PROBATION" | "EXIT";
interface HistoryEvent { at: string; kind: Kind; title: string; detail?: string; by?: string }
interface History { employee: { id: string; name: string; code: string; status: string }; events: HistoryEvent[] }

const KIND: Record<Kind, { icon: LucideIcon; className: string }> = {
  JOIN: { icon: UserPlus, className: "bg-[var(--color-success-bg)] text-[var(--color-success-fg)]" },
  FIELD: { icon: PenLine, className: "bg-brand-100 text-brand-700" },
  COMP: { icon: IndianRupee, className: "bg-[var(--color-accent-bg)] text-[var(--color-accent-fg)]" },
  SHIFT: { icon: CalendarClock, className: "bg-[var(--color-info-bg)] text-[var(--color-info-fg)]" },
  PROBATION: { icon: Briefcase, className: "bg-[var(--color-warning-bg)] text-[var(--color-warning-fg)]" },
  EXIT: { icon: LogOut, className: "bg-[var(--color-error-bg)] text-[var(--color-error-fg)]" },
};

// en-US, not en-GB: en-GB abbreviates September as "Sept", out of step with the API's "Sep".
const WHEN = new Intl.DateTimeFormat("en-US", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hourCycle: "h23" });

/** The employee's history timeline, newest first, from the Employee Report. */
export function EmployeeHistoryDialog({ employeeId, onClose }: { employeeId: string | null; onClose: () => void }) {
  const [data, setData] = useState<History | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!employeeId) return;
    let cancelled = false;
    setData(null);
    setError(null);
    api.get<History>(`/reports/hr/employees/${employeeId}/history`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((err) => {
        if (cancelled) return;
        logError("EmployeeHistoryDialog.load", err);
        setError(errMsg(err) ?? "Failed to load history");
      });
    return () => { cancelled = true; };
  }, [employeeId]);

  return (
    <Dialog open={!!employeeId} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><History size={16} /> {data ? `${data.employee.name} · ${data.employee.code}` : "Employee history"}</DialogTitle>
          <DialogDescription>Profile changes, compensation, shifts, probation and exit — newest first.</DialogDescription>
        </DialogHeader>
        <ErrorBanner message={error} />
        {!data && !error && (
          <div className="flex flex-col gap-3">
            {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}
          </div>
        )}
        {data && data.events.length === 0 && <p className="text-[13px] text-surface-500">No history recorded.</p>}
        {data && data.events.length > 0 && (
          <ol className="relative flex flex-col gap-3 border-l border-surface-200 pl-5">
            {data.events.map((e, i) => {
              const k = KIND[e.kind];
              const Icon = k.icon;
              return (
                <li key={i} className="relative">
                  <span className={cn("absolute -left-[31px] top-0 flex h-5 w-5 items-center justify-center rounded-full ring-2 ring-surface-0", k.className)}>
                    <Icon size={11} />
                  </span>
                  <div className="text-[13px] font-semibold text-surface-900">{e.title}</div>
                  {e.detail && <div className="text-[12px] text-surface-700">{e.detail}</div>}
                  <div className="text-[11px] text-surface-500">
                    {WHEN.format(new Date(e.at))}{e.by ? ` · by ${e.by}` : ""}
                  </div>
                </li>
              );
            })}
          </ol>
        )}
      </DialogContent>
    </Dialog>
  );
}
